/**
 * Traceability & Genealogy Section Component
 * POV-0120 / POV-0121: Section 7 of the Part Overview Panel
 *
 * Per PRD Section 11.3 + Completion Prompt Phase B:
 * - Traceability Policy display (read-only, sourced from Section 2)
 * - Genealogy Viewer: lot selector, upstream (where-from), downstream (where-used)
 * - Create Link modal (append-only, controlled — reason required)
 * - CSV export
 * - AS9100D Guidance banner (Clause 8.5.2)
 *
 * section_key: "traceability_genealogy"
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  GitBranch,
  Loader2,
  Info,
  Lock,
  Plus,
  Download,
  ArrowUp,
  ArrowDown,
  Package,
  Wrench,
  Truck,
  ShoppingCart,
  AlertTriangle,
  ChevronRight,
  ExternalLink,
  RefreshCw,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { ViewRelatedHistoryLink } from './HistorySection';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import api from '../../services/api';

const SECTION_KEY = 'traceability_genealogy';

/** Object type icons & labels */
const OBJECT_TYPE_CONFIG = {
  InventoryLot: { label: 'Inventory Lot', icon: Package, color: 'text-blue-600' },
  WorkOrderOp: { label: 'Work Order', icon: Wrench, color: 'text-amber-600' },
  ShipmentLine: { label: 'Shipment', icon: Truck, color: 'text-emerald-600' },
  PurchaseOrderLine: { label: 'Purchase Order', icon: ShoppingCart, color: 'text-violet-600' },
};

/** Relationship type labels */
const RELATIONSHIP_LABELS = {
  ConsumedIn: 'Consumed In',
  ProducedFrom: 'Produced From',
  Split: 'Split',
  Merged: 'Merged',
  ShippedAs: 'Shipped As',
  ReturnedAs: 'Returned As',
  AssembledInto: 'Assembled Into',
  DisassembledFrom: 'Disassembled From',
};

/** Traceability requirement badge */
const PolicyBadge = ({ requirement }) => {
  const config = {
    none: { label: 'None', className: 'bg-slate-100 text-slate-600 border-slate-200' },
    lot: { label: 'Lot Controlled', className: 'bg-blue-100 text-blue-700 border-blue-200' },
    serial: { label: 'Serial Controlled', className: 'bg-violet-100 text-violet-700 border-violet-200' },
  };
  const c = config[requirement] || config.none;
  return (
    <span className={cn('inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border', c.className)}>
      {c.label}
    </span>
  );
};

/** AS9100D Guidance Banner */
const GuidanceBanner = () => (
  <div className="mb-4 p-3 bg-sky-50 border border-sky-200 rounded-lg text-xs" data-testid="guidance-banner-traceability">
    <div className="flex items-start gap-2">
      <Info className="w-3.5 h-3.5 text-sky-600 mt-0.5 shrink-0" />
      <div>
        <p className="font-semibold text-sky-800">AS9100D Clause: 8.5.2 — Identification and Traceability</p>
        <ul className="mt-1 space-y-0.5 text-sky-700">
          <li>Identify product by suitable means throughout production and service provision</li>
          <li>Maintain traceability of lots/serials through the supply chain</li>
          <li>Record genealogy links (where-from, where-used) for compliance evidence</li>
        </ul>
      </div>
    </div>
  </div>
);

/** Traceability Policy Section (Read-Only from Section 2) */
const TraceabilityPolicy = ({ policy, onEditClick }) => {
  const attrs = policy?.required_lot_attributes || {};
  const requirement = policy?.traceability_requirement || 'none';
  const hasAttributes = Object.values(attrs).some(Boolean);

  return (
    <div className="mb-5" data-testid="traceability-policy">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
          Traceability Policy
          <span className="ml-1.5 text-[10px] font-normal text-slate-400">(Read-Only)</span>
        </h4>
      </div>
      <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-500">Requirement:</span>
          <PolicyBadge requirement={requirement} />
        </div>
        {requirement !== 'none' && (
          <div>
            <span className="text-xs text-slate-500">Required Attributes:</span>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {[
                { key: 'heat_lot', label: 'Heat/Lot' },
                { key: 'mfg_date', label: 'Mfg Date' },
                { key: 'expiry_date', label: 'Expiry' },
                { key: 'batch', label: 'Batch' },
              ].map(({ key, label }) => (
                <span
                  key={key}
                  className={cn(
                    'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] border',
                    attrs[key]
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                      : 'bg-slate-100 text-slate-400 border-slate-200'
                  )}
                >
                  {attrs[key] ? (
                    <CheckCircle2 className="w-2.5 h-2.5" />
                  ) : (
                    <XCircle className="w-2.5 h-2.5" />
                  )}
                  {label}
                </span>
              ))}
            </div>
          </div>
        )}
        {!hasAttributes && requirement === 'none' && (
          <p className="text-[11px] text-slate-400 italic">No traceability requirements defined</p>
        )}
        <button
          onClick={onEditClick}
          className="text-[11px] text-sky-600 hover:text-sky-700 hover:underline flex items-center gap-1 mt-1"
          data-testid="edit-in-identification-link"
        >
          Edit in Identification & Classification <ChevronRight className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
};

/** Genealogy Node Component (recursive tree node) */
const GenealogyNode = ({ node, depth = 0 }) => {
  const config = OBJECT_TYPE_CONFIG[node.object_type] || {
    label: node.object_type,
    icon: Package,
    color: 'text-slate-600',
  };
  const IconComp = config.icon;
  const relLabel = RELATIONSHIP_LABELS[node.relationship_type] || node.relationship_type;

  return (
    <div className={cn('border-l-2 border-slate-200 pl-3 py-1.5', depth === 0 && 'border-l-0 pl-0')}>
      <div className="flex items-start gap-2">
        <IconComp className={cn('w-3.5 h-3.5 mt-0.5 shrink-0', config.color)} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-xs font-medium text-slate-800">{node.object_id}</span>
            <span className="text-[10px] text-slate-400">{config.label}</span>
          </div>
          {node.relationship_type && (
            <span className="text-[10px] text-slate-500">
              {relLabel}
              {node.quantity != null && ` — ${node.quantity} EA`}
            </span>
          )}
          {node.label && (
            <p className="text-[10px] text-slate-400 mt-0.5">{node.label}</p>
          )}
        </div>
      </div>
      {node.children?.map((child, i) => (
        <GenealogyNode key={`${child.object_id}-${i}`} node={child} depth={depth + 1} />
      ))}
    </div>
  );
};

/** Genealogy Viewer Component */
const GenealogyViewer = ({ partId, links }) => {
  const [selectedLotId, setSelectedLotId] = useState('');
  const [genealogy, setGenealogy] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Derive available lots from existing links
  const availableLots = React.useMemo(() => {
    const lots = new Set();
    links.forEach((link) => {
      if (link.from_object_type === 'InventoryLot') lots.add(link.from_object_id);
      if (link.to_object_type === 'InventoryLot') lots.add(link.to_object_id);
    });
    return Array.from(lots).sort();
  }, [links]);

  const fetchGenealogy = useCallback(async (lotId) => {
    if (!lotId || !partId) return;
    setIsLoading(true);
    try {
      const response = await api.get(
        `/api/v1/traceability/part/${partId}/genealogy/${lotId}?direction=both&depth=3`
      );
      setGenealogy(response.data);
    } catch (err) {
      console.error('Failed to load genealogy:', err);
      toast.error('Failed to load genealogy data');
    } finally {
      setIsLoading(false);
    }
  }, [partId]);

  useEffect(() => {
    if (selectedLotId) {
      fetchGenealogy(selectedLotId);
    } else {
      setGenealogy(null);
    }
  }, [selectedLotId, fetchGenealogy]);

  const handleExportCsv = async () => {
    if (!selectedLotId) return;
    setIsExporting(true);
    try {
      const response = await api.get(
        `/api/v1/traceability/part/${partId}/genealogy/${selectedLotId}/export-csv`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `genealogy_${partId}_${selectedLotId}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Genealogy exported');
    } catch (err) {
      console.error('CSV export failed:', err);
      toast.error('Failed to export genealogy');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div data-testid="genealogy-viewer">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Genealogy Viewer</h4>
      </div>

      {/* Lot Selector */}
      <div className="mb-3">
        <Label className="text-xs text-slate-500 mb-1 block">Select Lot/Serial</Label>
        {availableLots.length > 0 ? (
          <Select value={selectedLotId} onValueChange={setSelectedLotId}>
            <SelectTrigger className="h-8 text-xs" data-testid="lot-selector">
              <SelectValue placeholder="Select a lot/serial..." />
            </SelectTrigger>
            <SelectContent>
              {availableLots.map((lotId) => (
                <SelectItem key={lotId} value={lotId} className="text-xs">
                  {lotId}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <p className="text-[11px] text-slate-400 italic p-2 bg-slate-50 rounded border border-slate-200">
            No lots/serials found in traceability links. Create a link to begin.
          </p>
        )}
      </div>

      {/* Genealogy Results */}
      {isLoading && (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="w-4 h-4 animate-spin text-slate-400 mr-2" />
          <span className="text-xs text-slate-400">Loading genealogy...</span>
        </div>
      )}

      {genealogy && !isLoading && (
        <div className="space-y-3">
          {/* Upstream */}
          <div className="p-3 bg-blue-50/50 border border-blue-200 rounded-lg" data-testid="upstream-section">
            <div className="flex items-center gap-1.5 mb-2">
              <ArrowUp className="w-3.5 h-3.5 text-blue-600" />
              <span className="text-xs font-semibold text-blue-800">
                Upstream — Where From ({genealogy.total_upstream})
              </span>
            </div>
            {genealogy.upstream.length > 0 ? (
              <div className="space-y-1">
                {genealogy.upstream.map((node, i) => (
                  <GenealogyNode key={`up-${node.object_id}-${i}`} node={node} />
                ))}
              </div>
            ) : (
              <p className="text-[11px] text-blue-400 italic">No upstream links found</p>
            )}
          </div>

          {/* Downstream */}
          <div className="p-3 bg-emerald-50/50 border border-emerald-200 rounded-lg" data-testid="downstream-section">
            <div className="flex items-center gap-1.5 mb-2">
              <ArrowDown className="w-3.5 h-3.5 text-emerald-600" />
              <span className="text-xs font-semibold text-emerald-800">
                Downstream — Where Used ({genealogy.total_downstream})
              </span>
            </div>
            {genealogy.downstream.length > 0 ? (
              <div className="space-y-1">
                {genealogy.downstream.map((node, i) => (
                  <GenealogyNode key={`down-${node.object_id}-${i}`} node={node} />
                ))}
              </div>
            ) : (
              <p className="text-[11px] text-emerald-400 italic">No downstream links found</p>
            )}
          </div>

          {/* Export Actions */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              onClick={handleExportCsv}
              disabled={isExporting}
              data-testid="export-csv-btn"
            >
              {isExporting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Download className="w-3 h-3 mr-1" />}
              Export Genealogy (CSV)
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

/** Create Traceability Link Modal */
const CreateLinkModal = ({ isOpen, onClose, partId, reasonCodes, onLinkCreated }) => {
  const [formData, setFormData] = useState({
    from_object_type: '',
    from_object_id: '',
    to_object_type: '',
    to_object_id: '',
    relationship_type: '',
    quantity: '',
    reference_label: '',
    reason_code: '',
    reason_text: '',
  });
  const [isSaving, setIsSaving] = useState(false);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.from_object_type || !formData.from_object_id || !formData.to_object_type || !formData.to_object_id || !formData.relationship_type || !formData.reason_code) {
      toast.error('Please fill all required fields');
      return;
    }
    if (formData.reason_code === 'other' && !formData.reason_text) {
      toast.error('Reason text is required when reason code is "Other"');
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        ...formData,
        quantity: formData.quantity ? parseFloat(formData.quantity) : null,
      };
      await api.post(`/api/v1/traceability/part/${partId}/links`, payload);
      toast.success('Traceability link created');
      onLinkCreated();
      onClose();
      setFormData({
        from_object_type: '', from_object_id: '', to_object_type: '', to_object_id: '',
        relationship_type: '', quantity: '', reference_label: '', reason_code: '', reason_text: '',
      });
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to create link';
      toast.error(msg);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-sm">
            <Lock className="w-3.5 h-3.5 text-amber-600" />
            Create Traceability Link
          </DialogTitle>
          <DialogDescription className="text-xs">
            Append-only — this link cannot be edited or deleted after creation.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* From */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">From Type *</Label>
              <Select value={formData.from_object_type} onValueChange={(v) => handleChange('from_object_type', v)}>
                <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>
                  {Object.entries(OBJECT_TYPE_CONFIG).map(([val, cfg]) => (
                    <SelectItem key={val} value={val} className="text-xs">{cfg.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">From ID *</Label>
              <Input className="h-8 text-xs" placeholder="e.g. LOT-2026-001" value={formData.from_object_id} onChange={(e) => handleChange('from_object_id', e.target.value)} />
            </div>
          </div>

          {/* Relationship */}
          <div>
            <Label className="text-xs">Relationship *</Label>
            <Select value={formData.relationship_type} onValueChange={(v) => handleChange('relationship_type', v)}>
              <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select..." /></SelectTrigger>
              <SelectContent>
                {Object.entries(RELATIONSHIP_LABELS).map(([val, label]) => (
                  <SelectItem key={val} value={val} className="text-xs">{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* To */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">To Type *</Label>
              <Select value={formData.to_object_type} onValueChange={(v) => handleChange('to_object_type', v)}>
                <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>
                  {Object.entries(OBJECT_TYPE_CONFIG).map(([val, cfg]) => (
                    <SelectItem key={val} value={val} className="text-xs">{cfg.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">To ID *</Label>
              <Input className="h-8 text-xs" placeholder="e.g. WO-2026-456" value={formData.to_object_id} onChange={(e) => handleChange('to_object_id', e.target.value)} />
            </div>
          </div>

          {/* Quantity & Label */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Quantity</Label>
              <Input className="h-8 text-xs" type="number" step="0.01" placeholder="e.g. 25" value={formData.quantity} onChange={(e) => handleChange('quantity', e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Reference Label</Label>
              <Input className="h-8 text-xs" placeholder="Description..." value={formData.reference_label} onChange={(e) => handleChange('reference_label', e.target.value)} />
            </div>
          </div>

          {/* Reason for change (CONTROLLED) */}
          <div className="border-t pt-3 mt-2">
            <div className="flex items-center gap-1.5 mb-2">
              <Lock className="w-3 h-3 text-amber-600" />
              <span className="text-xs font-semibold text-slate-700">Reason for Change</span>
              <span className="text-[10px] text-amber-600 font-medium">🔒 Controlled</span>
            </div>
            <Select value={formData.reason_code} onValueChange={(v) => handleChange('reason_code', v)}>
              <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select reason code *" /></SelectTrigger>
              <SelectContent>
                {reasonCodes.map((rc) => (
                  <SelectItem key={rc.code} value={rc.code} className="text-xs">{rc.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {formData.reason_code === 'other' && (
              <Textarea
                className="mt-2 text-xs"
                rows={2}
                placeholder="Explain the reason... (required for 'Other')"
                value={formData.reason_text}
                onChange={(e) => handleChange('reason_text', e.target.value)}
              />
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button size="sm" className="h-8 text-xs" onClick={handleSubmit} disabled={isSaving}>
            {isSaving && <Loader2 className="w-3 h-3 animate-spin mr-1" />}
            Create Link
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** Links Table */
const LinksTable = ({ links }) => {
  if (links.length === 0) return null;

  return (
    <div className="mb-4" data-testid="links-table">
      <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
        All Traceability Links ({links.length})
      </h4>
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-[10px] py-1.5 px-2">From</TableHead>
              <TableHead className="text-[10px] py-1.5 px-2">Relationship</TableHead>
              <TableHead className="text-[10px] py-1.5 px-2">To</TableHead>
              <TableHead className="text-[10px] py-1.5 px-2">Qty</TableHead>
              <TableHead className="text-[10px] py-1.5 px-2">Date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {links.slice(0, 20).map((link) => {
              const fromCfg = OBJECT_TYPE_CONFIG[link.from_object_type] || {};
              const toCfg = OBJECT_TYPE_CONFIG[link.to_object_type] || {};
              return (
                <TableRow key={link.traceability_link_id} className="text-xs">
                  <TableCell className="py-1.5 px-2">
                    <div className="flex items-center gap-1">
                      {fromCfg.icon && React.createElement(fromCfg.icon, { className: cn('w-3 h-3', fromCfg.color) })}
                      <span className="text-[11px]">{link.from_object_id}</span>
                    </div>
                  </TableCell>
                  <TableCell className="py-1.5 px-2">
                    <span className="text-[10px] text-slate-500">
                      {RELATIONSHIP_LABELS[link.relationship_type] || link.relationship_type}
                    </span>
                  </TableCell>
                  <TableCell className="py-1.5 px-2">
                    <div className="flex items-center gap-1">
                      {toCfg.icon && React.createElement(toCfg.icon, { className: cn('w-3 h-3', toCfg.color) })}
                      <span className="text-[11px]">{link.to_object_id}</span>
                    </div>
                  </TableCell>
                  <TableCell className="py-1.5 px-2 text-[11px]">
                    {link.quantity != null ? link.quantity : '—'}
                  </TableCell>
                  <TableCell className="py-1.5 px-2 text-[10px] text-slate-400">
                    {link.created_at ? new Date(link.created_at).toLocaleDateString() : '—'}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {links.length > 20 && (
          <div className="text-center py-1.5 text-[10px] text-slate-400 bg-slate-50 border-t">
            Showing 20 of {links.length} links
          </div>
        )}
      </div>
    </div>
  );
};

/** Main Traceability Section */
const TraceabilitySection = ({ partId, onViewRelatedHistory }) => {
  const [overview, setOverview] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reasonCodes, setReasonCodes] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const loadData = useCallback(async () => {
    if (!partId) return;
    setIsLoading(true);
    setError(null);
    try {
      const [overviewRes, configRes] = await Promise.all([
        api.get(`/api/v1/traceability/part/${partId}/overview`),
        api.get('/api/v1/traceability/controlled-fields'),
      ]);
      setOverview(overviewRes.data);
      setReasonCodes(configRes.data.reason_codes || []);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to load traceability data';
      setError(msg);
      console.error('Traceability load error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [partId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleEditInIdentification = () => {
    // Navigate to Identification & Classification section
    const section = document.getElementById('section-identification_classification');
    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-4 h-4 animate-spin text-slate-400 mr-2" />
        <span className="text-xs text-slate-400">Loading traceability data...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-6">
        <AlertTriangle className="w-5 h-5 text-red-400 mx-auto mb-2" />
        <p className="text-xs text-red-500 mb-2">{error}</p>
        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={loadData}>
          <RefreshCw className="w-3 h-3 mr-1" /> Retry
        </Button>
      </div>
    );
  }

  const policy = overview?.policy || {};
  const links = overview?.links || [];

  return (
    <div className="space-y-4" data-testid="traceability-section">
      {/* AS9100D Guidance Banner */}
      <GuidanceBanner />

      {/* Traceability Policy (Read-Only) */}
      <TraceabilityPolicy policy={policy} onEditClick={handleEditInIdentification} />

      {/* Actions Bar */}
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-500">
          {overview?.total_links || 0} traceability link{(overview?.total_links || 0) !== 1 ? 's' : ''} recorded
        </span>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={loadData}
          >
            <RefreshCw className="w-3 h-3 mr-1" /> Refresh
          </Button>
          <Button
            size="sm"
            className="h-7 text-xs"
            onClick={() => setShowCreateModal(true)}
            data-testid="create-link-btn"
          >
            <Plus className="w-3 h-3 mr-1" />
            <Lock className="w-2.5 h-2.5 mr-1 text-amber-200" />
            Add Link
          </Button>
        </div>
      </div>

      {/* Links Table */}
      <LinksTable links={links} />

      {/* Genealogy Viewer */}
      <GenealogyViewer partId={partId} links={links} />

      {/* View Related History */}
      <div className="pt-2">
        <ViewRelatedHistoryLink sectionKey={SECTION_KEY} onClick={onViewRelatedHistory} />
      </div>

      {/* Create Link Modal */}
      <CreateLinkModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        partId={partId}
        reasonCodes={reasonCodes}
        onLinkCreated={loadData}
      />
    </div>
  );
};

export default TraceabilitySection;
